// src/services/AlarmEngine.ts
import { globalState } from "../framework/globalState.ts";
import { subscribeRegister } from "./mqttClient.ts";
import { checkThreshold } from "./registerUtils.ts";

export interface IAlarmEntry {
  id: number;
  dbId: string;
  ts: string;
  registerName: string;
  value: number;
  metric: string;
  context: string;
  state: "Active" | "Cleared";
  priority: string;
  ackTime?: string;
}

interface RegisterWithDevice {
  _id: string;
  name: string;
  deviceName: string;
  deviceId: string;
  [key: string]: unknown;
}

export class AlarmEngine {
  static #instance: AlarmEngine | null = null;
  static get instance(): AlarmEngine {
    if (!AlarmEngine.#instance) {
      AlarmEngine.#instance = new AlarmEngine();
    }
    return AlarmEngine.#instance;
  }

  subscriptions: (() => void)[];
  initialized: boolean;
  alarmLog: IAlarmEntry[];
  activeAlarms: Map<string, IAlarmEntry>;
  nextAlarmId: number;
  onAlarmCreate: ((alarm: IAlarmEntry) => void) | null;
  onAlarmClear: ((alarm: IAlarmEntry) => void) | null;

  constructor() {
    this.subscriptions = [];
    this.initialized = false;

    // Alarm storage
    this.alarmLog = [];
    this.activeAlarms = new Map();
    this.nextAlarmId = 1;

    // Event callbacks
    this.onAlarmCreate = null;
    this.onAlarmClear = null;
  }

  async init(): Promise<void> {
    if (this.initialized) return;
    this.initialized = true;

    const siteWithData = globalState.currentSiteWithData as { site?: { _id: string }; devices?: { _id: string; name: string; registers?: { _id: string; name: string }[] }[] } | null;
    const site = siteWithData?.site;
    const registers: RegisterWithDevice[] = (siteWithData?.devices || []).flatMap(d =>
          (d.registers || []).map(r => ({ ...r, deviceName: d.name, deviceId: d._id }))
        );

    if (!site?._id || !registers.length) {
      console.warn("AlarmEngine: No site or registers");
      return;
    }

    // Subscribe to ALL registers
    registers.forEach(register => {
      const label = (register.name || "").trim();
      if (!label) return;

      const unsubscribe = subscribeRegister(site._id, register.deviceId, register._id, (payload: unknown) => {
        const value = Number((payload as { value?: number })?.value ?? 0);
        const context = this._extractContext(label);
        const metricKey = this._mapToMetricKey(label);

        if (metricKey) {
          const severity = checkThreshold(value, (register as any).meta);
          // Create or update alarm
          const alarm: IAlarmEntry = {
            id: this.nextAlarmId++,
            dbId: register._id,
            ts: new Date().toISOString(),
            registerName: register.name,
            value,
            metric: metricKey,
            context,
            state: "Active",
            priority: severity === 'critical' ? 'Critical' : severity === 'high' ? 'High' : 'Normal'
          };
          this.alarmLog.push(alarm);
          this.activeAlarms.set(register._id, alarm);

          // Fire event callback
          this.onAlarmCreate?.(alarm);
        }
      });

      this.subscriptions.push(unsubscribe);
    });

    console.log(`AlarmEngine: Monitoring ${registers.length} registers`);
  }

  // Return active alarms
  getActiveAlarms(): IAlarmEntry[] {
    return Array.from(this.activeAlarms.values());
  }

  // Return historical alarms (all logged)
  getHistoricalAlarms(): IAlarmEntry[] {
    return this.alarmLog.slice(); // return a shallow copy
  }

  // Acknowledge alarm by internal id
  acknowledge(alarmId: number): void {
    const alarm = this.alarmLog.find(a => a.id === alarmId);
    if (!alarm) return;
    alarm.ackTime = new Date().toISOString();
    alarm.state = "Cleared";
    this.activeAlarms.delete(alarm.dbId);
    this.onAlarmClear?.(alarm);
  }

  // Remove all subscriptions and reset
  cleanup(): void {
    this.subscriptions.forEach(u => typeof u === "function" && u());
    this.subscriptions = [];
    this.initialized = false;
    this.activeAlarms.clear();
    this.alarmLog = [];
    this.nextAlarmId = 1;
  }

  _extractContext(label: string): string {
    const match = label.match(/_(\d+)$/);
    const index = match ? match[1] : "1";
    if (label.includes("PV")) return `PV ${index}`;
    if (label.includes("BESS") || label.includes("PCS")) return `BESS ${index}`;
    if (label.includes("DG") || label.includes("Generator")) return `DG ${index}`;
    return `Unit ${index}`;
  }

  _mapToMetricKey(label: string): string | null {
    const map: Record<string, string> = {
      "Active Power": "Active Power",
      "Reactive Power": "Reactive Power",
      "Voltage": "Voltage",
      "Temperature": "Temperature",
      "Frequency": "Frequency",
      "Availability": "Availability",
      "DC Capacity": "DC Capacity",
      "AC Capacity": "AC Capacity",
      "Energy Today": "Energy Today",
      "Energy Total": "Energy Total",
      "BESS Active Power": "BESS Active Power",
      "BESS Reactive Power": "BESS Reactive Power",
      "SoC": "SoC",
      "BESS SOH": "BESS SOH",
      "BESS Voltage": "BESS Voltage",
      "BESS Current": "BESS Current",
      "BESS Temperature": "BESS Temperature",
      "DG Meter Active Power": "DG Meter Active Power",
      "DG Meter Frequency": "DG Meter Frequency",
      "DG Meter Voltage Phase A": "DG Meter Voltage Phase A",
      "DG Meter Current Phase A": "DG Meter Current Phase A",
      "DG Meter Oil Pressure": "DG Meter Oil Pressure",
      "DG Meter Fuel Level": "DG Meter Fuel Level",
      "DG Meter Reactive Power": "DG Meter Reactive Power",
    };

    for (const [key, value] of Object.entries(map)) {
      if (label.includes(key)) return value;
    }
    return null;
  }
}
